import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BrandStatus {
  brandSet: boolean;
  brandName: string | null;
  workspaceName: string | null;
}

/**
 * Persistent dashboard banner shown to admins whose workspace has not yet
 * set a customer-facing brand name (`workspaces.customEmailFromName`). The
 * banner has no dismiss control on purpose: when the field is left blank,
 * every outbound email/invoice/booking-page falls through to the workspace
 * name and ultimately the literal "Clarik" — a poor first impression
 * that the brand prompt exists to prevent (Task #559). The banner auto-
 * disappears the moment the field is populated, since the underlying query
 * polls /api/workspace/brand-status.
 *
 * Caller is responsible for gating on the `adminPanel.feature.brandingSettings`
 * permission so non-admins (who can't fix it from settings anyway) never see
 * the nag.
 */
export function BrandNameNudgeBanner() {
  const { data: status } = useQuery<BrandStatus>({
    queryKey: ["/api/workspace/brand-status"],
    refetchInterval: 30_000,
  });

  if (!status || status.brandSet) return null;

  return (
    <div
      className="rounded-lg border border-amber-300 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 px-4 py-3"
      data-testid="banner-brand-nudge"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-medium" data-testid="text-brand-nudge-title">
            Set your customer-facing brand name
          </div>
          <p className="text-muted-foreground mt-0.5 leading-snug">
            It signs the emails, invoices, and public booking page your
            clients see. Right now they'll see your workspace name (or a
            generic fallback) instead.
          </p>
        </div>
        <Link href="/admin?section=branding">
          <Button
            size="sm"
            className="gap-1.5 shrink-0"
            data-testid="button-brand-nudge-set"
          >
            Set brand name
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
